import React from "react";
import PropTypes from "prop-types";
import { FixedSizeList } from "react-window";
import { defaultTemplate } from "common/hoc";
import { MrbContentLoader } from "mrb/components/loader";
import { mergeCss } from "mrb/common/utils";
import { Image } from "common/components";
import { ContentTreeLoaderTemplate } from "themes/application/producer/components";
import currencyFormatter from "common/utils/currencyFormatter";
const ROW_HEIGHT = 48;
const INDENT_BASE = 16;
const INDENT_STEP = 22;
const THUMB_SIZE = 64;

// Main row shows the gross total (what the content actually earned across all producers).
function formatRevenue(row, t) {
    if (row.revenueMatched) {
        return currencyFormatter.format(row.totalRevenue || 0);
    }
    return t("APPLICATION.PRODUCER.CONTENT_TREE.REVENUE_UNAVAILABLE");
}

function formatRevenueShare(revenuePercentage, t) {
    if (revenuePercentage === null || revenuePercentage === undefined) {
        return t("APPLICATION.PRODUCER.CONTENT_TREE.REVENUE_SPLIT");
    }
    return t("APPLICATION.PRODUCER.CONTENT_TREE.REVENUE_SHARE", { percentage: revenuePercentage });
}

// ProducerPayment is the producer's net earnings computed by the BE (gross × ownership %).
function formatYourEarnings(row, t) {
    if (row.revenueMatched) {
        return currencyFormatter.format(row.revenue || 0);
    }
    return t("APPLICATION.PRODUCER.CONTENT_TREE.REVENUE_UNAVAILABLE");
}

function Chevron({ expanded }) {
    return (
        <i
            className={mergeCss(
                "u-icon u-icon--sml u-icon--arrow-down c-content-tree__chevron",
                !expanded && "u-rotate--180"
            )}
        />
    );
}

// One virtualized row. `data` carries the flattened rows + handlers (react-window itemData).
function Row({ index, style, data }) {
    const { rows, toggleNode, getCoverUrl, getContentLink, copyContentLink, openRevenuePopup, t } = data;
    const row = rows[index];
    // Quick fix: TorahAnytime / TorahAnytimePlaylist podcasts (PodcastContentType 2 & 3) don't
    // surface episodes here, so they come back with no children — make such a podcast a
    // non-expandable leaf instead of a chevron that opens to nothing. Default podcasts (with
    // episodes) and other row types are unchanged.
    const isLeaf = row.type === "track" || row.type === "episode";
    const clickable = !isLeaf && !(row.type === "podcast" && !row.hasChildren);
    // A row shows the copy action when its preview url can be built (e.g. a podcast/episode
    // missing its category slug yields null) AND it's linkable — disabled content, and any
    // content under a disabled artist/podcast group, never offers the link.
    const hasLink = row.linkable !== false && !!getContentLink(row);

    return (
        <div style={style}>
            <div
                className={mergeCss(
                    "c-content-tree__vrow",
                    `c-content-tree__vrow--${row.type}`,
                    clickable && "c-content-tree__vrow--clickable"
                )}
                style={{ paddingLeft: INDENT_BASE + row.depth * INDENT_STEP }}
                onClick={clickable ? () => toggleNode(row.id) : undefined}
            >
                <div className="c-content-tree__meta">
                    {clickable ? (
                        <Chevron expanded={row.expanded} />
                    ) : (
                        <span className="c-content-tree__chevron-spacer" />
                    )}
                    <Image
                        className="c-content-tree__thumb"
                        src={getCoverUrl(row.coverKind, row.coverImageId, THUMB_SIZE)}
                    />
                    <span className="c-content-tree__title">{row.title}</span>
                    {hasLink && (
                        <button
                            type="button"
                            className="c-content-tree__copy-link"
                            onClick={(e) => {
                                // Don't toggle the (clickable) row when copying its link.
                                e.stopPropagation();
                                copyContentLink(row);
                            }}
                        >
                            <i className="u-icon u-icon--sml u-icon--link--primary" />
                            {t("APPLICATION.PRODUCER.CONTENT_TREE.COPY_LINK")}
                        </button>
                    )}
                </div>
                {row.revenueMatched ? (
                    <button
                        type="button"
                        className="c-content-tree__revenue c-content-tree__revenue--clickable"
                        onClick={(e) => {
                            e.stopPropagation();
                            openRevenuePopup(row);
                        }}
                    >
                        {formatRevenue(row, t)}
                        <i className="u-icon u-icon--med u-icon--info c-content-tree__revenue-info" />
                    </button>
                ) : (
                    <span className="c-content-tree__revenue">{formatRevenue(row, t)}</span>
                )}
            </div>
        </div>
    );
}

function ContentTreeTemplate({ contentTreeViewStore, t }) {
    const rows = contentTreeViewStore.visibleRows;
    const popupNode = contentTreeViewStore.revenuePopupNode;
    // No AutoSizer in the project (house pattern uses fixed numeric heights). Cap the list at the
    // viewport but shrink to the content so small catalogs don't leave a tall empty card; large
    // catalogs scroll and virtualize within the cap.
    const maxHeight = typeof window !== "undefined" ? Math.max(360, window.innerHeight - 220) : 640;
    const listHeight = Math.min(maxHeight, Math.max(ROW_HEIGHT, rows.length * ROW_HEIGHT));

    return (
        <React.Fragment>
            <MrbContentLoader
                loaderStore={contentTreeViewStore.loaderStore}
                loaderRender={() => <ContentTreeLoaderTemplate />}
            >
                {() =>
                    contentTreeViewStore.isEmpty ? (
                        <div className="c-card--primary u-type--color--opaque u-type--center">
                            {t("APPLICATION.PRODUCER.CONTENT_TREE.EMPTY")}
                        </div>
                    ) : (
                        <div className="c-card--primary u-padd--none c-content-tree">
                            <FixedSizeList
                                height={listHeight}
                                width="100%"
                                itemCount={rows.length}
                                itemSize={ROW_HEIGHT}
                                style={{ overflowX: "hidden" }}
                                itemData={{
                                    rows,
                                    toggleNode: contentTreeViewStore.toggleNode,
                                    getCoverUrl: contentTreeViewStore.getCoverUrl,
                                    getContentLink: contentTreeViewStore.getContentLink,
                                    copyContentLink: contentTreeViewStore.copyContentLink,
                                    openRevenuePopup: contentTreeViewStore.openRevenuePopup,
                                    t,
                                }}
                            >
                                {Row}
                            </FixedSizeList>
                        </div>
                    )
                }
            </MrbContentLoader>
            {popupNode && (
                <div className="c-content-tree__revenue-overlay" onClick={contentTreeViewStore.closeRevenuePopup}>
                    <div className="c-content-tree__revenue-popup" onClick={(e) => e.stopPropagation()}>
                        <div className="c-content-tree__revenue-popup__header">
                            <span className="c-content-tree__revenue-popup__title">{popupNode.title}</span>
                            <button
                                type="button"
                                className="c-content-tree__revenue-popup__close"
                                onClick={contentTreeViewStore.closeRevenuePopup}
                            >
                                <i className="u-icon u-icon--sml u-icon--close" />
                            </button>
                        </div>
                        <div className="c-content-tree__revenue-popup__body">
                            <div className="c-content-tree__revenue-popup__row">
                                <span>{t("APPLICATION.PRODUCER.CONTENT_TREE.REVENUE_TOTAL")}</span>
                                <span>{formatRevenue(popupNode, t)}</span>
                            </div>
                            <div className="c-content-tree__revenue-popup__row">
                                <span>{t("APPLICATION.PRODUCER.CONTENT_TREE.REVENUE_YOUR_SHARE")}</span>
                                <span>{formatRevenueShare(popupNode.revenuePercentage, t)}</span>
                            </div>
                            <div className="c-content-tree__revenue-popup__row">
                                <span>{t("APPLICATION.PRODUCER.CONTENT_TREE.REVENUE_YOUR_EARNINGS")}</span>
                                <span>{formatYourEarnings(popupNode, t)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </React.Fragment>
    );
}

Chevron.propTypes = { expanded: PropTypes.bool };
Row.propTypes = { index: PropTypes.number, style: PropTypes.object, data: PropTypes.object };

export default defaultTemplate(ContentTreeTemplate);
